import React, { useState } from 'react';
import styled from 'styled-components';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 1000;
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transition: opacity 0.3s ease, visibility 0.3s ease;
  backdrop-filter: blur(4px);
`;

const Panel = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  border-radius: 24px 24px 0 0;
  padding: 0;
  transform: translateY(${props => props.isOpen ? '0' : '100%'});
  transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  max-height: 85vh;
  overflow: hidden;
  z-index: 1001;
  // box-shadow: 0 -20px 40px rgba(0, 0, 0, 0.15);
`;

const PanelHeader = styled.div`
  padding: 24px 24px 16px 24px;
  border-bottom: 1px solid #f1f5f9;
  /* same mid-tone as the chat header so both read the same under the host's dark-mode inversion */
  background: linear-gradient(135deg, #4677b5 0%, #3d6aa5 55%, #335d93 100%);
  color: white;
  position: relative;
`;

const DragIndicator = styled.div`
  width: 36px;
  height: 4px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 2px;
  margin: 0 auto 16px auto;
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const IconContainer = styled.div`
  width: 44px;
  height: 44px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
`;

const HeaderText = styled.div`
  flex: 1;
`;

const PanelTitle = styled.h3`
  margin: 0 0 4px 0;
  font-size: 20px;
  font-weight: 700;
  color: white;
`;

const PanelSubtitle = styled.p`
  margin: 0;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 400;
`;

const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  backdrop-filter: blur(10px);
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: scale(1.05);
  }
`;

const PanelBody = styled.div`
  padding: 24px;
`;

const FeedbackSection = styled.div`
  margin-bottom: 24px;
`;

const Label = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 12px;
`;

const LabelIcon = styled.div`
  width: 20px;
  height: 20px;
  background: linear-gradient(135deg, #3b74b8 0%, #275386 55%, #1b3c66 100%);
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 12px;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: 16px;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  outline: none;
  transition: all 0.2s ease;
  background: #f8fafc;
  
  &:focus {
    border-color: #275386;
    background: white;
    box-shadow: 0 0 0 4px rgba(39, 83, 134, 0.1);
  }
  
  &::placeholder {
    color: #9ca3af;
  }
`;

const CharCount = styled.div`
  text-align: right;
  font-size: 12px;
  color: #6b7280;
  margin-top: 8px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
`;

const Button = styled.button`
  flex: 1;
  padding: 10px 15px;
  border: none;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
  
  ${props => props.primary ? `
    background: linear-gradient(135deg, #3b74b8 0%, #275386 55%, #1b3c66 100%);
    color: white;
    box-shadow: 0 4px 15px rgba(39, 83, 134, 0.3);
    
    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(39, 83, 134, 0.4);
    }
    
    &:active {
      transform: translateY(0);
    }
  ` : `
    background: #f8fafc;
    color: #374151;
    border: 2px solid #e5e7eb;
    
    &:hover:not(:disabled) {
      background: #f1f5f9;
      border-color: #d1d5db;
      transform: translateY(-1px);
    }
  `}
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none !important;
  }
`;

const LoadingSpinner = styled.div`
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-right: 8px;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ButtonContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;


// Monochrome icons (currentColor) instead of emoji, so they survive the host's dark-mode inversion
const BoltIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M13.5 2 4.5 13.2c-.4.5 0 1.3.6 1.3H11l-1.2 7.1c-.1.7.8 1.1 1.3.5l8.4-11c.4-.5 0-1.3-.6-1.3h-5.6l1.3-7.1c.1-.7-.8-1.1-1.1-.7Z" fill="currentColor" />
  </svg>
);
const PlusIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
  </svg>
);
const PenIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M4 20h4L19 9l-4-4L4 16v4Z" stroke="currentColor" strokeWidth="2.2" strokeLinejoin="round" />
  </svg>
);

const FeedbackPanel = ({ isOpen, onClose, onSubmit, actionType, isLoading }) => {
  const [feedback, setFeedback] = useState('');
  const maxLength = 500;

  const handleSubmit = () => {
    onSubmit(feedback);
    setFeedback('');
  };

  const handleSkip = () => {
    onSubmit('');
    setFeedback('');
  };

  const handleClose = () => {
    setFeedback('');
    onClose();
  };

  const getConfig = () => {
    if (actionType === 'newSession') {
      return {
        title: 'Start New Session',
        subtitle: 'Create a fresh conversation',
        icon: <PlusIcon />,
        placeholder: 'Any feedback before starting fresh? Your input helps us improve...',
        labelIcon: <PenIcon />
      };
    } else {
      return {
        title: 'End Conversation',
        subtitle: 'Thank you for chatting with us',
        icon: <BoltIcon />,
        placeholder: 'How was your experience? We\'d love to hear your thoughts...',
        labelIcon: <PenIcon />
      };
    }
  };

  const config = getConfig();

  return (
    <>
      <Overlay isOpen={isOpen} onClick={handleClose} />
      <Panel isOpen={isOpen}>
        <PanelHeader>
          <DragIndicator />
          <HeaderContent>
            <IconContainer>
              {config.icon}
            </IconContainer>
            <HeaderText>
              <PanelTitle>{config.title}</PanelTitle>
              <PanelSubtitle>{config.subtitle}</PanelSubtitle>
            </HeaderText>
            <CloseButton onClick={handleClose}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </CloseButton>
          </HeaderContent>
        </PanelHeader>
        
        <PanelBody>
          <FeedbackSection>
            <Label>
              <LabelIcon>
                <span>{config.labelIcon}</span>
              </LabelIcon>
              Your Feedback
            </Label>
            <TextArea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder={config.placeholder}
              maxLength={maxLength}
              disabled={isLoading}
            />
            <CharCount>
              {/* {feedback.length}/{maxLength} characters */}
            </CharCount>
          </FeedbackSection>
          
          <ButtonGroup>
            <Button onClick={handleSkip} disabled={isLoading}>
              Skip for now
            </Button>
            <Button primary onClick={handleSubmit} disabled={isLoading}>
              <ButtonContent>
                {isLoading && <LoadingSpinner />}
                {isLoading ? 'Processing...' : 'Submit & Continue'}
              </ButtonContent>
            </Button>
          </ButtonGroup>
        </PanelBody>
      </Panel>
    </>
  );
};

export default FeedbackPanel; 